import React, { useState, useEffect } from 'react';
import { apiGet, apiPost } from '../../utils/api';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import Lottie from 'lottie-react';
import aiBrainAnimation from '../../assets/ai-brain.json';
import {
  Search, Wrench, Rocket, ArrowRight, Target, Users,
  Check, ChevronLeft, Plus, Loader2, Brain,
} from 'lucide-react';
import { PastSessions } from './shared';

export function ModeSelection({ companyName, onSelectStrategy, onSelectAdvisory, onSelectFocus, onSelectRecommendation, onNewSession }) {
  return (
    <div className="max-w-4xl mx-auto px-4 py-12" data-testid="mode-selection">
      <div className="text-center mb-10">
        <h1 className="text-3xl font-bold mb-2">Choose Your Mode</h1>
        <div className="flex items-center justify-center gap-3 flex-wrap">
          <p className="text-muted-foreground">How would you like MrCanvas to help you?</p>
          {onNewSession && (
            <button onClick={onNewSession} className="inline-flex items-center gap-1 text-xs font-medium text-odapto-orange hover:underline" data-testid="start-new-session-btn">
              <Plus className="w-3 h-3" /> Start New Session
            </button>
          )}
        </div>
      </div>
      <div className="grid gap-5 md:grid-cols-2">
        <button onClick={onSelectStrategy}
          className="p-6 rounded-2xl border-2 border-blue-500/30 bg-gradient-to-br from-blue-500/5 to-cyan-500/5 hover:border-blue-500/50 hover:shadow-lg transition-all text-left group"
          data-testid="mode-strategy">
          <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center mb-4"><Target className="w-6 h-6 text-blue-500" /></div>
          <h2 className="text-xl font-bold mb-2">Strategy</h2>
          <p className="text-sm text-muted-foreground mb-3">Guided interview. Perfect for entrepreneurs who want consultant-style strategic guidance.</p>
          <ul className="text-xs text-muted-foreground space-y-1">
            <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-blue-500" /> One-on-one strategic interview</li>
            <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-blue-500" /> Diagnosis with scored analysis</li>
            <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-blue-500" /> Auto-generated execution board</li>
          </ul>
          <div className="mt-4 flex items-center gap-1 text-blue-500 text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">Start Interview <ArrowRight className="w-4 h-4" /></div>
        </button>
        <button onClick={onSelectAdvisory}
          className="p-6 rounded-2xl border-2 border-violet-500/30 bg-gradient-to-br from-violet-500/5 to-purple-500/5 hover:border-violet-500/50 hover:shadow-lg transition-all text-left group"
          data-testid="mode-advisory">
          <div className="w-12 h-12 rounded-xl bg-violet-500/10 flex items-center justify-center mb-4"><Users className="w-6 h-6 text-violet-500" /></div>
          <h2 className="text-xl font-bold mb-2">Advisory Board</h2>
          <p className="text-sm text-muted-foreground mb-3">Build a virtual Board of Directors that provides collective strategic recommendations.</p>
          <ul className="text-xs text-muted-foreground space-y-1">
            <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-violet-500" /> AI-recommended advisors</li>
            <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-violet-500" /> Customizable board composition</li>
            <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-violet-500" /> Board-level strategic discussions</li>
          </ul>
          <div className="mt-4 flex items-center gap-1 text-violet-500 text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">Build My Board <ArrowRight className="w-4 h-4" /></div>
        </button>
        <button onClick={onSelectFocus}
          className="p-6 rounded-2xl border-2 border-indigo-500/30 bg-gradient-to-br from-indigo-500/5 to-purple-500/5 hover:border-indigo-500/50 hover:shadow-lg transition-all text-left group md:col-span-2"
          data-testid="mode-focus">
          <div className="flex gap-6 items-start">
            <div className="shrink-0">
              <div className="w-12 h-12 rounded-xl bg-indigo-500/10 flex items-center justify-center mb-4"><Target className="w-6 h-6 text-indigo-500" /></div>
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-bold mb-2">Sharpen Focus</h2>
              <p className="text-sm text-muted-foreground mb-3">Challenge your thinking before making your next strategic decision. Like sitting with experienced board members who challenge your thinking.</p>
              <div className="flex flex-wrap gap-2">
                {['Blind Spots', 'Challenge Assumptions', 'Reveal Insights', 'Uncover Risks', 'Identify Priorities'].map(m => (
                  <span key={m} className="text-xs px-2.5 py-1 rounded-full bg-indigo-500/10 text-indigo-600 dark:text-indigo-300 border border-indigo-500/20">{m}</span>
                ))}
              </div>
              <div className="mt-4 flex items-center gap-1 text-indigo-500 text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">Sharpen My Thinking <ArrowRight className="w-4 h-4" /></div>
            </div>
          </div>
        </button>
        <button onClick={onSelectRecommendation}
          className="p-6 rounded-2xl border-2 border-odapto-orange/30 bg-gradient-to-br from-odapto-orange/5 to-amber-500/5 hover:border-odapto-orange/50 hover:shadow-lg transition-all text-left group md:col-span-2"
          data-testid="mode-recommendation">
          <div className="flex gap-6 items-start">
            <div className="shrink-0">
              <div className="w-12 h-12 rounded-xl bg-odapto-orange/10 flex items-center justify-center mb-4"><Brain className="w-6 h-6 text-odapto-orange" /></div>
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-bold mb-2">Executive Recommendation</h2>
              <p className="text-sm text-muted-foreground mb-3">Get a consultant-grade strategic recommendation powered by your Company Intelligence and the Strategic Knowledge Library. Like having a McKinsey partner on call.</p>
              <div className="flex flex-wrap gap-2">
                {['Situation Analysis', 'Root Cause', 'Strategic Options', 'Execution Roadmap', 'KPIs'].map(m => (
                  <span key={m} className="text-xs px-2.5 py-1 rounded-full bg-odapto-orange/10 text-odapto-orange border border-odapto-orange/20">{m}</span>
                ))}
              </div>
              <div className="mt-4 flex items-center gap-1 text-odapto-orange text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">Get Recommendation <ArrowRight className="w-4 h-4" /></div>
            </div>
          </div>
        </button>
      </div>
      <PastSessions />
    </div>
  );
}

export function AdvisoryRecommendation({ onBack, onBoardReady }) {
  const [loading, setLoading] = useState(true);
  const [recs, setRecs] = useState([]);
  const [rationale, setRationale] = useState('');
  const [saving, setSaving] = useState(false);
  const [allAdvisors, setAllAdvisors] = useState([]);
  const [showLibrary, setShowLibrary] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [existingBoard, setExistingBoard] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const boardRes = await apiGet('/advisory/boards');
        if (boardRes.ok) {
          const bd = await boardRes.json();
          if (bd.exists) { setExistingBoard(bd); setSelectedIds(bd.advisor_ids || []); }
        }
        const [recRes, libRes] = await Promise.all([apiPost('/advisory/recommend', {}), apiGet('/advisory/advisors')]);
        if (recRes.ok && !cancelled) { const d = await recRes.json(); setRecs(d.recommendations || []); setRationale(d.board_rationale || ''); if (!existingBoard) setSelectedIds((d.recommendations || []).map(r => r.id)); }
        if (libRes.ok && !cancelled) { const d = await libRes.json(); setAllAdvisors(d.advisors || []); }
      } catch { toast.error('Failed to load recommendations'); }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  const toggleAdvisor = (id) => { setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]); };

  const handleSave = async () => {
    if (selectedIds.length === 0) { toast.error('Select at least one advisor'); return; }
    setSaving(true);
    try {
      const res = await apiPost('/advisory/boards', { advisor_ids: selectedIds });
      if (res.ok) { toast.success('Advisory Board saved!'); onBoardReady(); }
      else toast.error('Failed to save');
    } catch { toast.error('Connection error'); }
    setSaving(false);
  };

  if (loading) return (
    <div className="flex items-center justify-center h-full"><div className="text-center">
      <Lottie animationData={aiBrainAnimation} loop autoplay style={{ width: 64, height: 64, margin: '0 auto' }} />
      <p className="text-muted-foreground mt-4">Analysing your company to recommend the best advisors...</p>
    </div></div>
  );

  return (
    <div className="max-w-4xl mx-auto px-4 py-8" data-testid="advisory-recommendation">
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4"><ChevronLeft className="w-4 h-4" /> Back to modes</button>
      <div className="text-center mb-8">
        <h1 className="text-2xl font-bold mb-2">{existingBoard ? 'Your Advisory Board' : 'Recommended Advisory Board'}</h1>
        {rationale && <p className="text-sm text-muted-foreground max-w-xl mx-auto">{rationale}</p>}
      </div>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 mb-6">
        {recs.map(adv => {
          const sel = selectedIds.includes(adv.id);
          return (
            <div key={adv.id} className={`p-4 rounded-xl border-2 transition-all ${sel ? 'border-odapto-orange bg-odapto-orange/5' : 'border-border'}`}>
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold shrink-0" style={{ backgroundColor: adv.avatar_color }}>{adv.name.split(' ').map(n => n[0]).join('')}</div>
                <div className="flex-1 min-w-0"><h3 className="font-semibold text-sm">{adv.name}</h3><p className="text-xs text-muted-foreground">{adv.specialty}</p></div>
                <div className="text-xs font-bold text-odapto-orange">{adv.match_score}%</div>
              </div>
              <p className="text-xs text-muted-foreground mb-3">{adv.reason}</p>
              <button onClick={() => toggleAdvisor(adv.id)} className={`w-full py-1.5 rounded-lg text-xs font-medium transition-all ${sel ? 'bg-odapto-orange text-white' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`}>
                {sel ? <><Check className="w-3 h-3 inline mr-1" /> Selected</> : 'Add to Board'}
              </button>
            </div>
          );
        })}
      </div>
      <div className="text-center space-y-3 mb-6">
        <button onClick={() => setShowLibrary(!showLibrary)} className="text-sm text-odapto-orange hover:underline" data-testid="customize-board-btn">{showLibrary ? 'Hide Library' : 'Browse All Advisors'}</button>
      </div>
      {showLibrary && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 mb-6">
          {allAdvisors.filter(a => !recs.find(r => r.id === a.id)).map(adv => {
            const sel = selectedIds.includes(adv.id);
            return (
              <div key={adv.id} className={`p-3 rounded-lg border transition-all ${sel ? 'border-odapto-orange bg-odapto-orange/5' : 'border-border'}`}>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0" style={{ backgroundColor: adv.avatar_color }}>{adv.name.split(' ').map(n => n[0]).join('')}</div>
                  <div className="flex-1 min-w-0"><p className="font-medium text-xs">{adv.name}</p><p className="text-[10px] text-muted-foreground">{adv.specialty}</p></div>
                  <button onClick={() => toggleAdvisor(adv.id)} className={`p-1 rounded-md ${sel ? 'text-odapto-orange' : 'text-muted-foreground hover:text-foreground'}`}>{sel ? <Check className="w-4 h-4" /> : <Plus className="w-4 h-4" />}</button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <div className="text-center">
        <Button onClick={handleSave} disabled={saving || selectedIds.length === 0} className="bg-odapto-orange hover:bg-odapto-orange-hover text-white px-8 py-3 text-base" data-testid="save-board-btn">
          {saving ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Saving...</> : <>{existingBoard ? 'Update Board & Start Meeting' : `Save Board & Start Meeting (${selectedIds.length})`}</>}
        </Button>
      </div>
    </div>
  );
}
